import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { usePhaseTemplateForm } from "../../../hooks/annual-planner/usePhaseTemplateForm";
import { useRillContext } from "../../../rill/RillReact/share/base";
import { getRandomColor } from "../../../utils/annual-planner-helpers";
import Modal from "../../modal-new";
import { PhaseTrackColumn } from "./parts/PhaseTrackColumn";

// Blank slate: Training and Nutrition are symmetric - both start with one
// empty row so the two columns render identically from the moment the
// modal opens.
const buildBlankInitialState = () => ({
  phase_template_name: "",
  phases: [
    { phaseName: "", phaseColor: getRandomColor(), track: "training" },
    { phaseName: "", phaseColor: getRandomColor(), track: "nutrition" },
  ],
});

// "Save current plan as template" (the Annual Planner header's dropdown) -
// seeds with whatever the athlete actually has on their calendar right now
// instead of blank rows, so the coach only has to name it. Phases here
// already carry a real phaseColor from the athlete's plan; only `track`
// needs stamping, same as legacy (trackless) template phases default to
// "training" elsewhere in this feature.
const buildSeededInitialState = (athlete) => {
  const trainingPhases = (
    athlete?.annualPlan?.trainingPhases?.phases || []
  ).map((phase) => ({ ...phase, track: "training" }));
  const nutritionPhases = (
    athlete?.annualPlan?.nutritionPhases?.phases || []
  ).map((phase) => ({ ...phase, track: "nutrition" }));

  return {
    phase_template_name: "",
    phases: [...trainingPhases, ...nutritionPhases],
  };
};

export const AddPhaseTemplateModal = ({ query, organizationCoachId }) => {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const { locals } = useRillContext();
  const formRef = useRef();

  const {
    phase_template_name,
    trainingRows,
    nutritionRows,
    emptyPhasesState,
    handleFieldChange,
    handlePhaseChange,
    handleAddPhase,
    handleRemovePhase,
    handleSubmit,
  } = usePhaseTemplateForm({
    getInitialState: () =>
      query?.seedFromAthlete
        ? buildSeededInitialState(locals.athlete)
        : buildBlankInitialState(),
    onSubmit: (payload) => {
      let body = payload;
      if (organizationCoachId) {
        body = { ...body, organizationCoachId };
      }

      submit("/actions/annual-planner/phase-template", {
        method: "POST",
        body,
      });
    },
  });

  return (
    <Modal
      className="annual-planner-modal add-phase-template-modal"
      title={t("addPhaseTemplateModal.title")}
    >
      <div className="body">
        <form
          id="add-phase-template-form"
          className="annual-planner-modal-form add-phase-template-modal-form"
          onSubmit={handleSubmit}
          encType="multipart/form-data"
          ref={formRef}
        >
          <div role="wrapper" className="template-name">
            <label>{t("addPhaseTemplateModal.phaseGroupNameLabel")}</label>
            <input
              type={"text"}
              name="phase_template_name"
              className="phase-template-name text-input"
              placeholder={t("addPhaseTemplateModal.placeholderName")}
              defaultValue={phase_template_name}
              onChange={({ target: { value } }) =>
                handleFieldChange("phase_template_name", value)
              }
            />
          </div>
          <div className="phase-tracks-columns">
            <PhaseTrackColumn
              title={t("phaseTracks.training")}
              rows={trainingRows}
              placeholderPhaseName={t(
                "addPhaseTemplateModal.placeholderPhaseName"
              )}
              addNewLabel={t("addPhaseTemplateModal.addNew")}
              onAddPhase={() => handleAddPhase("training")}
              onChangePhase={handlePhaseChange}
              onRemovePhase={handleRemovePhase}
            />
            <PhaseTrackColumn
              title={t("phaseTracks.nutrition")}
              rows={nutritionRows}
              placeholderPhaseName={t(
                "addPhaseTemplateModal.placeholderPhaseName"
              )}
              addNewLabel={t("addPhaseTemplateModal.addNew")}
              onAddPhase={() => handleAddPhase("nutrition")}
              onChangePhase={handlePhaseChange}
              onRemovePhase={handleRemovePhase}
            />
          </div>
        </form>
      </div>
      <footer className="phase-template-footer">
        <button
          type="button"
          role="button"
          data-action="cancel"
          onClick={() =>
            submit("/actions/refresh?", {
              method: "GET",
              body: {
                "modal[type]": "",
              },
            })
          }
        >
          {t("common:cancel")}
        </button>
        <button
          type="submit"
          form="add-phase-template-form"
          role="button"
          data-button-type="primary"
          disabled={emptyPhasesState}
        >
          {t("common:save")}
        </button>
      </footer>
    </Modal>
  );
};
